import React, { useState } from 'react';
import { Switch } from '@headlessui/react';
import { AlertTriangle, ChevronsLeftRightEllipsis } from 'lucide-react';
import { ConnectionParam, PlaybookParamValue } from '@/types/playbook';
import LexicalRenderer from './LexicalRenderer';

interface ConnectionParametersFormProps {
  connectionJSON: {
    params: ConnectionParam[];
    values: PlaybookParamValue[];
  } | null;
  onUpdateConnectionValues: (values: PlaybookParamValue[]) => void;
}

const ConnectionParametersForm: React.FC<ConnectionParametersFormProps> = ({
  connectionJSON,
  onUpdateConnectionValues,
}) => {
  const params = connectionJSON?.params || [];
  const values = connectionJSON?.values || [];
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Create a memoized map for quick lookup of values by name
  const valuesMap = React.useMemo(() => {
    return values.reduce((acc, val) => {
      acc[val.name] = val;
      return acc;
    }, {} as Record<string, PlaybookParamValue>);
  }, [values]);

  // Validate a parameter value against its regex
  const validateParam = (param: ConnectionParam, value: string): string | null => {
    if (!param.regexValidation || !value) return null;
    
    try {
      const regex = new RegExp(param.regexValidation);
      if (!regex.test(value)) {
        return 'Value does not match the required format';
      }
    } catch (error) {
      return 'Invalid validation pattern';
    }
    
    return null;
  };

  // Handle input value changes
  const handleValueChange = (paramName: string, value: string) => {
    const paramDef = params.find(p => p.name === paramName);
    if (!paramDef) return;

    // Check if the parameter already exists in values
    const existingValueIndex = values.findIndex(v => v.name === paramName);
    let newValues: PlaybookParamValue[];

    if (existingValueIndex >= 0) {
      // Update existing value
      newValues = values.map(v => 
        v.name === paramName ? { ...v, value } : v
      );
    } else {
      // Add new value
      newValues = [...values, { 
        name: paramName, 
        value, 
        isPipelineVariable: false 
      }];
    }

    onUpdateConnectionValues(newValues);

    // Validate the new value
    const error = validateParam(paramDef, value);
    setValidationErrors(prev => ({
      ...prev,
      [paramName]: error || '',
    }));
  };

  // Handle pipeline variable toggle
  const handlePipelineVariableToggle = (paramName: string, isPipelineVariable: boolean) => {
    // Check if the parameter already exists in values
    const existingValueIndex = values.findIndex(v => v.name === paramName);
    let newValues: PlaybookParamValue[];

    if (existingValueIndex >= 0) {
      // Update existing value
      newValues = values.map(v =>
        v.name === paramName ? { ...v, isPipelineVariable } : v
      );
    } else {
      // Add new value
      newValues = [...values, { 
        name: paramName, 
        value: '', 
        isPipelineVariable 
      }];
    }

    onUpdateConnectionValues(newValues);

    // Clear validation error when switching to pipeline variable
    if (isPipelineVariable) {
      setValidationErrors(prev => ({
        ...prev,
        [paramName]: '',
      }));
    }
  };

  if (params.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <ChevronsLeftRightEllipsis className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
        <div className="text-gray-500 dark:text-gray-400 font-medium mb-2">
          No connection parameters configured
        </div>
        <div className="text-sm text-gray-400 dark:text-gray-500">
          Configure connection parameters in the Connection Settings tab to see them here.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          <ChevronsLeftRightEllipsis className="w-5 h-5" />
          Connection Parameters
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Configure the connection parameters for this playbook. Values can be set directly or marked as pipeline variables.
        </p>
      </div>

      <div className="grid gap-4">
        {params.map((paramDef, index) => {
          const paramValue = valuesMap[paramDef.name];
          
          const hasError = validationErrors[paramDef.name];
          const isRequired = !paramDef.isOptional;
          
          // Get current values, defaulting to empty/false if not found
          const currentValueText = paramValue?.value || '';
          const isPipelineVar = paramValue?.isPipelineVariable || false;

          return (
            <div
              key={`${paramDef.name}-${index}`}
              className="flex items-center p-3 rounded-lg border transition-colors relative group border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-md"
            >
              <div className="flex-1 space-y-3">
                {/* Parameter Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-900 dark:text-white pl-1">
                      {paramDef.name}
                      {isRequired && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </label>
                  </div>

                  {/* Pipeline Variable Toggle */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Pipeline Variable
                    </span>
                    <Switch
                      checked={isPipelineVar}
                      onChange={(enabled) => handlePipelineVariableToggle(paramDef.name, enabled)}
                      className={`${
                        isPipelineVar
                          ? 'bg-blue-600'
                          : 'bg-gray-200 dark:bg-gray-700'
                      } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`}
                    >
                      <span
                        className={`${
                          isPipelineVar ? 'translate-x-5' : 'translate-x-1'
                        } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </div>
                </div>

                {/* Input Field */}
                <div className="space-y-1">
                  <input
                    type="text"
                    value={isPipelineVar ? '' : currentValueText}
                    onChange={(e) => handleValueChange(paramDef.name, e.target.value)}
                    placeholder={
                      isPipelineVar
                        ? 'To be set on the Pipeline'
                        : paramDef.exampleValue || `Enter ${paramDef.name.toLowerCase()}...`
                    }
                    disabled={isPipelineVar}
                    className={`w-full px-3 py-2 text-sm border rounded-md transition-colors disabled:bg-gray-50 disabled:text-gray-500 disabled:shadow-none dark:disabled:border-gray-600 dark:disabled:bg-gray-500/20 ${
                      hasError
                        ? 'border-red-300 dark:border-red-600 focus:border-red-500 focus:ring-red-500'
                        : isPipelineVar
                        ? 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        : 'border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500'
                    } bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1`}
                  />
                  
                  {/* Validation Error */}
                  {hasError && (
                    <div className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                      <AlertTriangle className="w-3 h-3" />
                      {hasError}
                    </div>
                  )}

                  {/* Description */}
                  {paramDef.description && (
                    <div className="text-xs text-gray-600 dark:text-gray-400 pl-1 pt-2 pb-1">
                      <LexicalRenderer value={paramDef.description} />
                    </div>
                  )}
                </div>

              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConnectionParametersForm;
